import { useTranslation } from "@privacy/translations";
import { DynamicHistory } from "../dynamicHistory";
import type { BaseHistoryEntry } from "../dynamicHistory/types";
import type { Form } from "../dynamicForm/types";
import type { RegistrationHistoryCustomEntry } from "../api/registrations.types";
import { logUnhandled } from "../api/logError";
import { PersonIcon3 } from "./icons";
import { RegistrationStatusChip } from "./RegistrationStatus";
import { isSharedRegistrationEvent, SharedRegistrationEventEntry } from "./SharedRegistrationEventEntry";
import { AuditPostponedEntry } from "./AuditPostponedEntry";

const EMPTY_USER_ID = "00000000-0000-0000-0000-000000000000";

export const RegistrationCustomEntry = ({
  entry,
  form,
  organisation,
  responsibleHeader,
}: {
  entry: BaseHistoryEntry & RegistrationHistoryCustomEntry;
  form?: Form;
  organisation?: string;
  responsibleHeader?: string;
}) => {
  const { t } = useTranslation();
  if (isSharedRegistrationEvent(entry)) return <SharedRegistrationEventEntry entry={entry} form={form} />;
  switch (entry.customType) {
    case "registrationStatus":
    case "registrationAuditStatus": {
      const isStatus = entry.customType === "registrationStatus";
      return (
        <DynamicHistory.BeforeAndAfterEntry
          {...entry}
          header={t(isStatus ? "Registration.Status" : "RegistrationHistory.AuditStatus")}
          before={<RegistrationStatusChip status={entry.oldValue} />}
          after={<RegistrationStatusChip status={entry.newValue} />}
        />
      );
    }
    case "registrationAuditPostponed":
      return <AuditPostponedEntry entry={entry} organisation={organisation} />;
    case "registrationName":
      return (
        <DynamicHistory.BeforeAndAfterEntry
          {...entry}
          header={t("Registration.Name")}
          before={<DynamicHistory.SnapshotValueText>{entry.oldValue}</DynamicHistory.SnapshotValueText>}
          after={<DynamicHistory.SnapshotValueText>{entry.newValue}</DynamicHistory.SnapshotValueText>}
        />
      );
    case "registrationResponsible":
      return (
        <DynamicHistory.BeforeAndAfterEntry
          {...entry}
          header={responsibleHeader ?? t("RegistrationGrid.ResponsibleHeader")}
          before={
            !entry.oldValue || entry.oldValue.id === EMPTY_USER_ID ? (
              <DynamicHistory.EmptyValue />
            ) : (
              <DynamicHistory.SnapshotValueText Icon={PersonIcon3}>
                {entry.oldValue.firstName} {entry.oldValue.lastName}
              </DynamicHistory.SnapshotValueText>
            )
          }
          after={
            <DynamicHistory.SnapshotValueText Icon={PersonIcon3}>
              {entry.newValue.firstName} {entry.newValue.lastName}
            </DynamicHistory.SnapshotValueText>
          }
        />
      );
    default:
      return logUnhandled(entry, "custom entry");
  }
};
